import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/contexts/AuthContext";
import { Search, X, Package, Plus } from "lucide-react";

export default function RelatedProductsTab({ data, onChange }) {
  const [searchQ, setSearchQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [relatedData, setRelatedData] = useState([]);
  const [addType, setAddType] = useState("products");

  const relatedIds = data.related_product_ids || [];

  const relatedKey = relatedIds.join(",");
  useEffect(() => {
    if (!relatedIds.length) { setRelatedData([]); return; }
    Promise.all(relatedIds.map(id => axios.get(`${API}/products/${id}`).then(r => r.data).catch(() => null)))
      .then(list => setRelatedData(list.filter(Boolean)));
  }, [relatedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = async () => {
    if (!searchQ.trim()) return;
    setSearching(true);
    try {
      const res = await axios.get(`${API}/products/search?q=${encodeURIComponent(searchQ)}`);
      setResults(res.data.filter(p => !relatedIds.includes(p.id) && p.id !== data.id));
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const addProduct = (product) => {
    if (relatedIds.includes(product.id) || product.id === data.id) return;
    onChange("related_product_ids", [...relatedIds, product.id]);
    setRelatedData(prev => [...prev, product]);
    setResults(prev => prev.filter(p => p.id !== product.id));
  };

  const removeProduct = (id) => {
    onChange("related_product_ids", relatedIds.filter(x => x !== id));
    setRelatedData(prev => prev.filter(p => p.id !== id));
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-5">
        <div className="w-5 h-5 bg-orange-100 rounded flex items-center justify-center">
          <span className="text-orange-600 text-xs font-bold">7</span>
        </div>
        <h2 className="text-base font-semibold text-slate-900">Related Products</h2>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5">
        {/* Add type selection */}
        <div className="flex gap-4 mb-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <div
              onClick={() => setAddType("products")}
              className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                addType === "products" ? "border-orange-500" : "border-slate-300"
              }`}
            >
              {addType === "products" && <div className="w-2 h-2 rounded-full bg-orange-500" />}
            </div>
            <span className="text-sm text-slate-700">Add Products</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <div
              onClick={() => setAddType("category")}
              className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                addType === "category" ? "border-orange-500" : "border-slate-300"
              }`}
            >
              {addType === "category" && <div className="w-2 h-2 rounded-full bg-orange-500" />}
            </div>
            <span className="text-sm text-slate-700">Add Category</span>
          </label>
        </div>

        {/* Search */}
        <div className="flex gap-2 mb-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={searchQ}
              onChange={e => setSearchQ(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleSearch()}
              placeholder="Search products or collections..."
              className="w-full pl-9 pr-4 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20"
            />
          </div>
          <button
            type="button"
            onClick={handleSearch}
            disabled={searching}
            className="bg-orange-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-orange-600 transition-all disabled:opacity-60"
          >
            {searching ? "Searching..." : "Search"}
          </button>
        </div>

        {/* Search Results */}
        {results.length > 0 && (
          <div className="border border-slate-200 rounded-lg mb-4 overflow-hidden">
            <div className="bg-slate-50 px-3 py-2 text-xs font-medium text-slate-500 border-b border-slate-200">
              Search Results
            </div>
            {results.map(p => (
              <div
                key={p.id}
                className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 border-b last:border-0 border-slate-100 cursor-pointer"
                onClick={() => addProduct(p)}
              >
                <div className="w-8 h-8 rounded border border-slate-200 overflow-hidden flex-shrink-0 bg-slate-50">
                  {p.main_image ? (
                    <img src={p.main_image} alt={p.item_name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="w-4 h-4 text-slate-300" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-800 truncate">{p.item_name}</p>
                  <p className="text-xs text-slate-400">{p.product_sku || p.id}</p>
                </div>
                <button className="text-orange-500 hover:text-orange-600">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Added Products */}
        <div>
          <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-3">
            Added Products ({relatedData.length})
          </h4>

          {relatedData.length === 0 ? (
            <div className="text-center py-8 border-2 border-dashed border-slate-200 rounded-xl">
              <Package className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-400">No related products added yet</p>
              <p className="text-xs text-slate-400 mt-1">Search and add products above</p>
            </div>
          ) : (
            <div className="space-y-2">
              {relatedData.map(p => (
                <div
                  key={p.id}
                  className="flex items-center gap-3 px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-lg"
                >
                  <div className="w-10 h-10 rounded-lg border border-slate-200 overflow-hidden flex-shrink-0 bg-white">
                    {p.main_image ? (
                      <img src={p.main_image} alt={p.item_name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="w-4 h-4 text-slate-300" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{p.item_name}</p>
                    <p className="text-xs text-slate-500">SKU: {p.product_sku || "—"}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeProduct(p.id)}
                    className="text-slate-400 hover:text-red-500 p-1 rounded hover:bg-red-50 transition-all"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {relatedData.length > 0 && (
          <button
            type="button"
            onClick={() => { setSearchQ(""); handleSearch(); }}
            className="mt-3 flex items-center gap-1.5 text-sm text-orange-600 hover:text-orange-700 font-medium"
          >
            <Plus className="w-4 h-4" />
            Add More Products
          </button>
        )}
      </div>
    </div>
  );
}
